import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/layout/AdminSidebar";

const AdminQuizSettingsPage = () => {
    const [settings, setSettings] = useState({
        species_required: "",
        questions_total: "",
        correct_required: "",
        time_limit_minutes: "",
    });

    const [initialSettings, setInitialSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [success, setSuccess] = useState({});
    const [error, setError] = useState("");

    const settingKeys = [
        "species_required",
        "questions_total",
        "correct_required",
        "time_limit_minutes",
    ];

    const mapResponseToValue = (key, data) => {
        switch (key) {
            case "species_required":
                return data.numberOfRequiredSpecies;
            case "questions_total":
                return data.totalNumberOfQuestions;
            case "correct_required":
                return data.numberOfCorrectAnswersRequired;
            case "time_limit_minutes":
                return data.timeLimit;
            default:
                return "";
        }
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const results = {};

                await Promise.all(
                    settingKeys.map(async (key) => {
                        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quiz-settings/${key}`);
                        const data = await res.json();
                        results[key] = mapResponseToValue(key, data);
                    })
                );

                setSettings(results);
                setInitialSettings(results);
            } catch (err) {
                setError("Kunde inte hämta inställningar");
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (value === "" || /^[0-9]+$/.test(value)) {
            setSettings((prev) => ({
                ...prev,
                [name]: value,
            }));

            // reset success state when editing
            setSuccess((prev) => ({ ...prev, [name]: false }));
        }
    };

    const updateSetting = async (settingName) => {
        try {
            setSaving((prev) => ({ ...prev, [settingName]: true }));
            setError("");

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/quiz-settings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    settingName,
                    value: parseInt(settings[settingName], 10),
                }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data?.error || "Fel vid sparning");
            }

            setInitialSettings((prev) => ({
                ...prev,
                [settingName]: settings[settingName],
            }));

            setSuccess((prev) => ({ ...prev, [settingName]: true }));
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving((prev) => ({ ...prev, [settingName]: false }));
        }
    };

    const isChanged = (key) => {
        return (
            settings[key] !== "" &&
            settings[key] !== initialSettings[key]
        );
    };

    if (loading) {
        return <div className="p-10">Laddar...</div>;
    }

    const renderField = (label, key) => (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                </label>
                <input
                    type="text"
                    name={key}
                    value={settings[key]}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest"
                />
            </div>

            <div className="flex items-center gap-3">
                <button
                    onClick={() => updateSetting(key)}
                    disabled={!isChanged(key) || saving[key]}
                    className="h-[48px] px-6 bg-forest text-white rounded-lg font-semibold hover:bg-leaf transition duration-200 disabled:opacity-50"
                >
                    {saving[key] ? "Sparar..." : "Spara"}
                </button>

                {/* Success indicator */}
                {success[key] && (
                    <span className="text-green-600 text-xl">✔</span>
                )}
            </div>
        </div>
    );

    return (
        <div className="flex min-h-screen bg-gray-50 font-body">
            <AdminSidebar />

            <div className="flex-1 md:ml-64 flex flex-col">
                <main className="flex-grow">
                    <div className="bg-gradient-to-r from-forest to-leaf py-12">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                            <h1 className="font-heading text-4xl font-bold text-white sm:text-5xl">
                                Quizinställningar
                            </h1>
                            <p className="mt-4 text-lg text-white/90">
                                Uppdatera krav och tidsgränser för quizet
                            </p>
                        </div>
                    </div>

                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="font-heading text-2xl font-bold text-charcoal">
                                    Inställningar
                                </h2>
                            </div>

                            <div className="p-6 space-y-6">
                                {renderField("Antal arter som krävs", "species_required")}
                                {renderField("Totalt antal frågor", "questions_total")}
                                {renderField("Antal rätt för godkänt", "correct_required")}
                                {renderField("Tidsgräns (minuter)", "time_limit_minutes")}

                                {error && (
                                    <div className="text-red-600 font-medium">
                                        {error}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminQuizSettingsPage;